import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';

import { environment } from '../../../environments/environment';

export interface GoogleCseExecuteContext {
  type?: string;
  value?: string;
  queries?: string[];
}

export interface GoogleSearchView {
  href: string;
  embedUrl: SafeResourceUrl;
}

/** Same Google web-search URL the CSE "Search on Google" link uses. */
export function buildGoogleSearchUrl(
  query: string,
  cx: string,
  embed = false,
): string {
  const params = new URLSearchParams({
    client: 'ms-google-coop',
    q: query,
    cx,
    newwindow: '1',
  });
  if (embed) {
    params.set('igu', '1');
  }
  return `https://www.google.com/search?${params.toString()}`;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleCseService {
  constructor(
    private snackBar: MatSnackBar,
    private sanitizer: DomSanitizer,
  ) { }

  getEmbeddedSearch(
    query: string,
    context?: GoogleCseExecuteContext,
  ): GoogleSearchView | null {
    const trimmed = String(query || '').trim();
    if (!trimmed) {
      return null;
    }

    const cx = environment.googleSearchEngineId;
    if (!cx) {
      this.snackBar.open(
        'Google Search is not configured. Set googleSearchEngineId in environment.',
        'Close',
        { duration: 5000, panelClass: ['snackbar-error'] },
      );
      return null;
    }

    this.debug(context, trimmed);

    const href = buildGoogleSearchUrl(trimmed, cx);
    const embedHref = buildGoogleSearchUrl(trimmed, cx, true);
    return {
      href,
      embedUrl: this.sanitizer.bypassSecurityTrustResourceUrl(embedHref),
    };
  }

  private debug(
    context: GoogleCseExecuteContext | undefined,
    query: string,
  ): void {
    if (environment.production) {
      return;
    }
    if (context?.type) {
      console.debug('[Google CSE] Search type:', context.type);
    }
    if (context?.value !== undefined) {
      console.debug('[Google CSE] Search value:', context.value);
    }
    if (context?.queries) {
      console.debug('[Google CSE] Generated queries:', context.queries);
    }
    console.debug('[Google CSE] Active query:', query);
  }
}
